import { useState, useEffect, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Alert, CircularProgress, Button, Menu, MenuItem, Paper, Chip, Tabs, Tab } from '@mui/material';
import { Download } from 'lucide-react';
import type { Fund } from '@lingaai/types';
import { getPreviousPeriod, useFundSubmissions, useFundPeriods } from '@lingaai/shared-hooks';
import { CollectionNarrative } from './CollectionNarrative';
import { CompanyStatusTable } from './CompanyStatusTable';
import { SubmissionDetailDrawer } from '../../../components/SubmissionDetailDrawer';
import { EmailTimelineDrawer } from '../../../components/EmailTimelineDrawer';
import { SubmissionsEmptyState } from '../../../components/SubmissionsEmptyState';
import { downloadCSV, downloadExcel, formatMetricValue } from '@lingaai/shared-utils';

interface CurrentPeriodTabProps {
  fund: Fund;
  viewToggle?: ReactNode;
}

export function CurrentPeriodTab({ fund, viewToggle }: CurrentPeriodTabProps) {
  const [searchParams] = useSearchParams();
  const currentPeriod = fund.reporting_frequency ? getPreviousPeriod(fund.reporting_frequency, fund.reporting_timezone || 'UTC') : null;

  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod || '');
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
  const [timelineSubmissionId, setTimelineSubmissionId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // Auto-open submission drawer from query param
  useEffect(() => {
    const submissionId = searchParams.get('submission');
    if (submissionId) {
      setSelectedSubmissionId(submissionId);
    }
  }, [searchParams]);

  const { data: submissions, isLoading } = useFundSubmissions(fund.id, selectedPeriod);
  const { data: periodOptions = [], isLoading: periodsLoading } = useFundPeriods(fund.id);

  // Auto-select first period if current period not in list
  useEffect(() => {
    if (periodOptions.length > 0 && !periodOptions.includes(selectedPeriod) && !periodsLoading) {
      const firstPeriod = periodOptions[0];
      if (firstPeriod) {
        setSelectedPeriod(firstPeriod);
      }
    }
  }, [periodOptions, selectedPeriod, periodsLoading]);

  if (!fund.reporting_frequency || !currentPeriod) {
    return <Alert severity="warning">This fund does not have a reporting frequency configured. Please configure the reporting settings to enable data collection.</Alert>;
  }

  // Show empty state if no periods exist (no submissions have ever been created)
  if (!periodsLoading && periodOptions.length === 0) {
    return <SubmissionsEmptyState fund={fund} />;
  }

  // Calculate deadline based on reporting_day_of_month
  const getDeadline = (period: string): Date => {
    // Parse period (e.g., "Q3 2025")
    const [quarter, year] = period.split(' ');
    if (!quarter || !year) {
      return new Date();
    }

    const quarterNum = parseInt(quarter.replace('Q', ''));

    // Calculate the month after the quarter ends
    const monthAfterQuarter = quarterNum * 3 + 1; // Q1->4, Q2->7, Q3->10, Q4->1
    const deadlineYear = monthAfterQuarter > 12 ? parseInt(year) + 1 : parseInt(year);
    const deadlineMonth = monthAfterQuarter > 12 ? 1 : monthAfterQuarter;

    return new Date(deadlineYear, deadlineMonth - 1, fund.reporting_day_of_month || 15);
  };

  const deadline = getDeadline(selectedPeriod);

  const handleExport = (format: 'csv' | 'excel') => {
    if (!submissions || submissions.length === 0) {
      alert('No submissions available to export');
      setExportAnchor(null);
      return;
    }

    // Flatten all metrics from all submissions
    const exportData: any[] = [];
    submissions.forEach((submission: any) => {
      if (submission.metrics && submission.metrics.length > 0) {
        submission.metrics.forEach((metric: any) => {
          exportData.push({
            Company: submission.portfolio_companies?.name || submission.company_name || 'Unknown',
            Period: submission.period || selectedPeriod,
            Metric: metric.metric_definitions?.name || 'Unknown',
            Value: formatMetricValue(metric.value),
            Confidence: metric.confidence ? `${(metric.confidence * 100).toFixed(0)}%` : '-',
            Source: metric.source_type || '-',
            Reviewed: metric.reviewed ? 'Yes' : 'No',
            Status: submission.status || '-',
          });
        });
      }
    });

    if (exportData.length === 0) {
      alert('No metrics found to export');
      setExportAnchor(null);
      return;
    }

    const filename = `${fund.name.replace(/\s+/g, '-')}-${selectedPeriod.replace(' ', '-')}`;

    if (format === 'csv') {
      downloadCSV(exportData, filename);
    } else {
      downloadExcel(exportData, filename);
    }

    setExportAnchor(null);
  };

  const handleSendReminder = async (companyId: string) => {
    // TODO: Implement reminder email/notification
    console.log('Sending reminder to company:', companyId);
    // Could trigger an edge function or send via email service
  };

  // Filter submissions based on status
  const filterSubmissions = (subs: typeof submissions | undefined, filter: string | null) => {
    if (!subs || !filter) return subs;

    switch (filter) {
      case 'responded':
        return subs.filter((s) => s.submitted_at !== null);
      case 'not_responded':
        return subs.filter((s) => s.submitted_at === null && s.status !== 'draft');
      case 'completed':
        return subs.filter((s) => s.status === 'completed');
      case 'needs_review':
        return subs.filter((s) => s.status === 'needs_review');
      case 'processing':
        return subs.filter((s) => s.status === 'processing');
      default:
        return subs;
    }
  };

  if (isLoading && !submissions) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show empty state if no submissions exist
  if (!isLoading && (!submissions || submissions.length === 0)) {
    return <SubmissionsEmptyState fund={fund} />;
  }

  const selectedPeriodIndex = periodOptions.indexOf(selectedPeriod);

  return (
    <Box sx={{ height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header: Toggle + Period Tabs + Export */}
      <Paper sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        {viewToggle && <Box sx={{ pl: 2, pr: 1 }}>{viewToggle}</Box>}
        <Tabs
          value={selectedPeriodIndex >= 0 ? selectedPeriodIndex : 0}
          onChange={(_, newValue) => {
            const period = periodOptions[newValue];
            if (period) setSelectedPeriod(period);
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flex: 1,
            '& .MuiTab-root': {
              minWidth: 100,
              textTransform: 'none',
            },
          }}
        >
          {periodOptions.map((period, index) => (
            <Tab
              key={period}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {period}
                  {period === currentPeriod && <Chip label="Current" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />}
                </Box>
              }
              value={index}
            />
          ))}
        </Tabs>
        <Box sx={{ px: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download size={14} strokeWidth={1} />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
            sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.5 }}
          >
            Export
          </Button>
          <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
            <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
            <MenuItem onClick={() => handleExport('excel')}>Export as Excel</MenuItem>
          </Menu>
        </Box>
      </Paper>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {submissions && submissions.length > 0 ? (
          <>
            <CollectionNarrative submissions={submissions} deadline={deadline} onFilterStatus={(status) => setStatusFilter(status)} />
            <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
              Company Status
              {statusFilter && <Chip label={`Filtered: ${statusFilter}`} size="small" onDelete={() => setStatusFilter(null)} sx={{ ml: 2 }} />}
            </Typography>
            <CompanyStatusTable
              submissions={filterSubmissions(submissions, statusFilter) || []}
              onViewSubmission={setSelectedSubmissionId}
              onViewTimeline={setTimelineSubmissionId}
              onSendReminder={handleSendReminder}
            />
          </>
        ) : (
          <Alert severity="info">No portfolio companies found for this fund. Add companies to start collecting data.</Alert>
        )}
      </Box>

      <SubmissionDetailDrawer
        submissionId={selectedSubmissionId}
        open={!!selectedSubmissionId}
        onClose={() => setSelectedSubmissionId(null)}
        onNavigate={setSelectedSubmissionId}
        submissionIds={submissions?.map((s) => s.submission_id).filter((id): id is string => !!id) || []}
        fundId={fund.id}
      />

      <EmailTimelineDrawer submissionId={timelineSubmissionId} open={!!timelineSubmissionId} onClose={() => setTimelineSubmissionId(null)} />
    </Box>
  );
}
